import React, { useState } from "react";
import {
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Avatar,
  Typography,
  Divider,
} from "@mui/material";
import { useLocation } from "react-router-dom";

// Icons
import MenuIcon from "@mui/icons-material/Menu";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EventIcon from "@mui/icons-material/Event";
import PeopleIcon from "@mui/icons-material/People";
import SecurityIcon from "@mui/icons-material/Security";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import MapIcon from "@mui/icons-material/Map";
import BadgeIcon from "@mui/icons-material/Badge";
import ContactsIcon from "@mui/icons-material/Contacts";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";

function MobileMenu(props) {
  const [openDrawer, setOpenDrawer] = useState(false);
  const location = useLocation();
  const currentUser = props.currentUser;
  const navigate = props.navigate;
  const logOut = props.logOut;
  const toggleColorMode = props.toggleColorMode;
  const theme = props.theme;

  const currentPath = location.pathname;
  const isActive = path => currentPath === path;

  const NavItem = ({ path, icon, label, onClick }) => {
    const active = isActive(path);
    return (
      <ListItem disablePadding sx={{ mb: 0.5 }}>
        <ListItemButton
          onClick={() => {
            if (onClick) onClick();
            else navigate(path);
            setOpenDrawer(false);
          }}
          sx={{
            borderRadius: "12px",
            mx: 1,
            bgcolor: active
              ? theme.palette.mode === "dark"
                ? "rgba(144, 202, 249, 0.16)"
                : "rgba(25, 118, 210, 0.08)"
              : "transparent",
            color: active ? "primary.main" : "text.primary",
            "&:hover": {
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(0, 0, 0, 0.04)",
            },
          }}>
          <ListItemIcon
            sx={{
              color: active ? "primary.main" : "text.secondary",
              minWidth: 40,
            }}>
            {icon}
          </ListItemIcon>
          <ListItemText
            primary={label}
            primaryTypographyProps={{
              fontWeight: active ? 600 : 500,
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        PaperProps={{
          sx: {
            width: 280,
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(18, 18, 18, 0.85)"
                : "rgba(255, 255, 255, 0.85)",
            backdropFilter: "blur(20px)",
            borderLeft: "1px solid",
            borderColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.05)"
                : "rgba(0, 0, 0, 0.05)",
          },
        }}>
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          {/* User Profile Header */}
          <Box
            sx={{
              p: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.03)"
                  : "rgba(0, 0, 0, 0.02)",
            }}>
            {currentUser ? (
              <>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: "primary.main",
                    fontSize: "2rem",
                  }}>
                  {currentUser.username?.charAt(0).toUpperCase()}
                </Avatar>
                <Box textAlign="center">
                  <Typography variant="h6" fontWeight="bold">
                    {currentUser.username}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {currentUser.role === 1
                      ? "Администратор"
                      : currentUser.role === 9
                      ? "Менеджер по продажам"
                      : currentUser.role === 2
                      ? "Логист"
                      : `Роль: ${currentUser.role}`}
                  </Typography>
                </Box>
              </>
            ) : (
              <Avatar sx={{ width: 64, height: 64, bgcolor: "grey.400" }}>
                <PersonIcon fontSize="large" sx={{ color: "white" }} />
              </Avatar>
            )}
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Navigation Links */}
          <List sx={{ flexGrow: 1, px: 1 }}>
            {currentUser?.role === 3 && (
              <>
                <NavItem
                  path="/today"
                  icon={<CalendarTodayIcon />}
                  label="Сегодня"
                />
                <NavItem path="/tomorrow" icon={<EventIcon />} label="Завтра" />
              </>
            )}

            {currentUser?.role === 7 && (
              <>
                <NavItem
                  path="/add_pacient"
                  icon={<PersonAddIcon />}
                  label="Добавить пациента"
                />
                <NavItem
                  path="/store_pacients"
                  icon={<PeopleIcon />}
                  label="История пациентов"
                />
              </>
            )}

            {currentUser?.role === 6 && (
              <NavItem
                path="/agentpanel"
                icon={<DashboardIcon />}
                label="Панель Агента"
              />
            )}

            {currentUser?.role === 4 && (
              <>
                <NavItem
                  path="/mod"
                  icon={<SecurityIcon />}
                  label="Панель Модератора"
                />
                <NavItem path="/lpu" icon={<LocalHospitalIcon />} label="ЛПУ" />
              </>
            )}

            {currentUser?.role === 8 && (
              <>
                <NavItem path="/lpu" icon={<LocalHospitalIcon />} label="ЛПУ" />
                <NavItem path="/order-map" icon={<MapIcon />} label="Карта заказов" />
              </>
            )}

            {currentUser?.role === 1 && (
              <NavItem
                path="/admin/"
                icon={<DashboardIcon />}
                label="Панель Администратора"
              />
            )}

            {currentUser?.role !== 3 &&
              currentUser?.role !== 6 &&
              currentUser && (
                <NavItem
                  path="/neworder"
                  icon={<AddCircleIcon />}
                  label="Создать заказ"
                  onClick={() => window.open("/neworder")}
                />
              )}

            {currentUser?.role === 1 && (
              <NavItem
                path="/logisticians"
                icon={<BadgeIcon />}
                label="Логисты"
              />
            )}

            {currentUser?.role === 2 && (
              <NavItem
                path="/home?my=1"
                icon={<AssignmentIndIcon />}
                label="Личные заказы"
                onClick={() => window.open("/home?my=1", "_blank")}
              />
            )}

            {[1, 9].includes(currentUser?.role) && (
              <NavItem
                path="/leads"
                icon={<ContactsIcon />}
                label="Лиды (Юр лица)"
              />
            )}

            {(currentUser?.role === 1 ||
              (currentUser?.role === 2 && currentUser?.company === 1)) && (
              <NavItem
                path="/messages"
                icon={<ChatBubbleIcon />}
                label="Сообщения"
              />
            )}
          </List>

          <Divider sx={{ my: 1 }} />

          {/* Bottom Actions */}
          <List sx={{ px: 1, pb: 2 }}>
            {currentUser ? (
              <>
                <NavItem
                  path="/profile"
                  icon={<PersonIcon />}
                  label="Профиль"
                />
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => {
                      logOut();
                      setOpenDrawer(false);
                    }}
                    sx={{ borderRadius: "12px", mx: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40, color: "error.main" }}>
                      <LogoutIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Выйти"
                      primaryTypographyProps={{ color: "error" }}
                    />
                  </ListItemButton>
                </ListItem>
              </>
            ) : (
              <NavItem path="/login" icon={<LoginIcon />} label="Войти" />
            )}

            <ListItem disablePadding>
              <ListItemButton
                onClick={toggleColorMode}
                sx={{ borderRadius: "12px", mx: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {theme.palette.mode === "dark" ? (
                    <Brightness7Icon />
                  ) : (
                    <Brightness4Icon />
                  )}
                </ListItemIcon>
                <ListItemText primary="Тема" />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>

      <IconButton onClick={() => setOpenDrawer(!openDrawer)} color="inherit">
        <MenuIcon />
      </IconButton>
    </>
  );
}

export default MobileMenu;
